import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from '@mui/material';

interface Price {
  priceId: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
}

interface SimplePriceSelectorProps {
  prices: Price[];
  onPriceSelect?: (price: Price) => void;
  defaultInterval?: 'month' | 'year';
}

// Helper function to format price
const formatPrice = (price: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(price / 100); // Convert from cents to dollars
};

export const SimplePriceSelector: React.FC<SimplePriceSelectorProps> = ({
  prices,
  onPriceSelect,
  defaultInterval = 'month',
}) => {
  const [selectedInterval, setSelectedInterval] = useState<'month' | 'year'>(defaultInterval);

  // Find prices for each interval
  const monthlyPrice = prices.find(p => p.interval === 'month');
  const yearlyPrice = prices.find(p => p.interval === 'year');

  // Calculate savings for annual plan
  const calculateSavings = () => {
    if (!monthlyPrice || !yearlyPrice) return 0;
    const yearlyEquivalent = monthlyPrice.amount * 12;
    return yearlyEquivalent - yearlyPrice.amount;
  };

  const savings = calculateSavings();
  const savingsPercentage = monthlyPrice && yearlyPrice 
    ? Math.round((savings / (monthlyPrice.amount * 12)) * 100)
    : 0;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newInterval = event.target.value as 'month' | 'year';
    setSelectedInterval(newInterval);
    
    // Notify parent of selection
    if (onPriceSelect) {
      const selectedPrice = newInterval === 'month' ? monthlyPrice : yearlyPrice;
      if (selectedPrice) {
        onPriceSelect(selectedPrice);
      }
    }
  };

  // If only one price option, show it without toggle
  if (prices.length === 1) {
    const singlePrice = prices[0];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" component="span">
          {formatPrice(singlePrice.amount, singlePrice.currency)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          per {singlePrice.interval}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl component="fieldset">
        <RadioGroup
          value={selectedInterval}
          onChange={handleChange}
          aria-label="subscription interval"
        >
          {monthlyPrice && (
            <FormControlLabel
              value="month"
              control={<Radio size="small" />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(monthlyPrice.amount, monthlyPrice.currency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    / month
                  </Typography>
                </Box>
              }
              sx={{ mb: 1 }}
            />
          )}
          
          {yearlyPrice && (
            <FormControlLabel
              value="year"
              control={<Radio size="small" />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(yearlyPrice.amount, yearlyPrice.currency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    / year
                  </Typography>
                  {savings > 0 && (
                    <Chip
                      label={`Save ${savingsPercentage}%`}
                      size="small"
                      color="success"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              }
            />
          )}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};